import { MutableRefObject } from 'react';
import Handsontable from 'handsontable';
import useRefCallback from '../../../../utils/react/useRefCallback';

/**
 * Hooks used by the Milanote table editing plugins (MilanoteEditingPlugin & MilanoteCellEditor).
 *
 * NOTE: `useStableCallback` allows us to pass these functions into the HotTable config (which is only
 * initialised once) without worrying about the function identity changing on each render.
 */
export const useTableEditingHandlers = ({
    hotTableInstanceRef,
    showTitle,
    elementId,
    isCurrentlyEditingTableCell,
    dispatchStartEditingTitle,
    dispatchStartEditingTableCell,
    dispatchStartEditingTableGrid,
    setCurrentlyEditingCellEditorCoords,
}: {
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    showTitle: boolean;
    elementId: string;
    isCurrentlyEditingTableCell: boolean;
    dispatchStartEditingTitle: (elementId: string) => void;
    dispatchStartEditingTableCell: (elementId: string, cellRect: DOMRect | undefined) => void;
    dispatchStartEditingTableGrid: () => void;
    setCurrentlyEditingCellEditorCoords: (cellCoords: { row: number; col: number } | null) => void;
}) => {
    /**
     * Edits the table title.
     */
    const jumpToTitle = useRefCallback(() => {
        showTitle && requestAnimationFrame(() => dispatchStartEditingTitle(elementId));
    });

    /**
     * Starts editing the cell editor.
     */
    const startEditingCellEditor = useRefCallback((cellCoords) => {
        setCurrentlyEditingCellEditorCoords(cellCoords);

        requestAnimationFrame(() => {
            if (!isCurrentlyEditingTableCell) {
                // TODO: Once table editor is updated to TipTap, we can use the specific caret client coord
                //       instead of the cell client coord
                const cellRect = hotTableInstanceRef.current
                    ?.getCell(cellCoords.row, cellCoords.col)
                    ?.getBoundingClientRect();

                dispatchStartEditingTableCell(elementId, cellRect);
            }
        });
    });

    /**
     * Finishes editing the cell editor.
     */
    const finishEditingCellEditor = useRefCallback(() => {
        setCurrentlyEditingCellEditorCoords(null);

        requestAnimationFrame(() => {
            if (isCurrentlyEditingTableCell) dispatchStartEditingTableGrid();
        });
    });

    return {
        jumpToTitle,
        startEditingCellEditor,
        finishEditingCellEditor,
    };
};
